// Lightweight in-app logger.
// - Always emits via console (no env gating).
// - Forwards warn/error records to a backend sink in batches.
// - Provides scoped child loggers and global handler installation.

import { getApiUrl, isNativeApp } from './apiHelpers';

const MAX_BATCH = 20;
const FLUSH_INTERVAL_MS = 5000;
const MAX_MESSAGE_LEN = 2000;
const MAX_STACK_LEN = 8000;

let userId = null;
let sessionId;
try {
  sessionId = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
} catch {
  sessionId = `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// Computed per-record rather than at module load: on Capacitor, window.Capacitor
// may not be injected yet when logger.js first runs. Caching here would lock
// platform to 'web' for the whole session in that race.
const detectPlatform = () => {
  if (typeof window === 'undefined') return 'unknown';
  if (window.electronAPI) return 'electron';
  if (isNativeApp()) {
    const cap = window.Capacitor;
    return cap && cap.getPlatform ? cap.getPlatform() : 'capacitor';
  }
  return 'web';
};

const build = process.env.REACT_APP_BUILD_ID || 'dev';

const queue = [];
let flushTimer = null;
let inFlight = false;

const truncate = (s, n) => (typeof s === 'string' && s.length > n ? s.slice(0, n) : s);

const normalizeError = (err) => {
  if (!err) return null;
  if (err instanceof Error) {
    return {
      name: err.name || 'Error',
      message: truncate(err.message || '', MAX_MESSAGE_LEN),
      stack: truncate(err.stack || '', MAX_STACK_LEN),
    };
  }
  if (typeof err === 'string') {
    return { name: 'String', message: truncate(err, MAX_MESSAGE_LEN), stack: null };
  }
  // ErrorEvent or PromiseRejectionEvent fallback
  try {
    return {
      name: err.name || 'Unknown',
      message: truncate(String(err.message ?? err), MAX_MESSAGE_LEN),
      stack: truncate(err.stack || '', MAX_STACK_LEN),
    };
  } catch {
    return { name: 'Unknown', message: 'unserializable error', stack: null };
  }
};

const consoleFor = (level) => {
  if (level === 'error') return console.error;
  if (level === 'warn') return console.warn;
  if (level === 'info') return console.info;
  return console.debug || console.log;
};

const emit = (level, scope, message, errOrCtx, maybeCtx) => {
  let error = null;
  let ctx = null;
  if (errOrCtx instanceof Error || (errOrCtx && (errOrCtx.message || errOrCtx.stack))) {
    error = normalizeError(errOrCtx);
    ctx = maybeCtx ?? null;
  } else {
    ctx = errOrCtx ?? null;
  }

  const tag = scope ? `[${level}][${scope}]` : `[${level}]`;
  const print = consoleFor(level);
  if (error && ctx) print(tag, message, error, ctx);
  else if (error) print(tag, message, error);
  else if (ctx) print(tag, message, ctx);
  else print(tag, message);

  if (level === 'warn' || level === 'error') {
    enqueue({
      ts: new Date().toISOString(),
      level,
      scope: scope || null,
      message: truncate(String(message ?? ''), MAX_MESSAGE_LEN),
      ctx: ctx || null,
      error,
      build,
      platform: detectPlatform(),
      sessionId,
      userId,
    });
  }
};

const enqueue = (record) => {
  queue.push(record);
  if (queue.length >= MAX_BATCH) {
    flush();
  } else if (!flushTimer) {
    flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
  }
};

const flush = (useBeacon = false) => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (inFlight || queue.length === 0) return;
  const batch = queue.splice(0, MAX_BATCH);
  const payload = JSON.stringify({ records: batch });
  const url = `${getApiUrl()}/api/client-errors`;

  if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
    try {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    } catch {
      // ignore — best effort on unload
    }
    return;
  }

  inFlight = true;
  // No retry on failure: dropping a batch is preferable to a feedback loop
  // where logging failures generate more logs.
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: true,
  }).catch(() => {}).finally(() => {
    inFlight = false;
    if (queue.length > 0 && !flushTimer) {
      flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
    }
  });
};

const makeLogger = (scope) => ({
  debug: (msg, ctx) => emit('debug', scope, msg, ctx),
  info: (msg, ctx) => emit('info', scope, msg, ctx),
  warn: (msg, errOrCtx, ctx) => emit('warn', scope, msg, errOrCtx, ctx),
  error: (msg, errOrCtx, ctx) => emit('error', scope, msg, errOrCtx, ctx),
  child: (childScope) => makeLogger(scope ? `${scope}.${childScope}` : childScope),
});

export const logger = makeLogger(null);

export const setUserContext = ({ userId: id }) => {
  userId = id ?? null;
};

export const clearUserContext = () => {
  userId = null;
};

let handlersInstalled = false;
export const installGlobalErrorHandlers = () => {
  if (handlersInstalled || typeof window === 'undefined') return;
  handlersInstalled = true;

  window.addEventListener('error', (e) => {
    logger.error('window.error', e.error || e.message || 'unknown', {
      filename: e.filename,
      lineno: e.lineno,
      colno: e.colno,
    });
  });

  window.addEventListener('unhandledrejection', (e) => {
    logger.error('unhandledrejection', e.reason ?? 'unknown');
  });

  // Best-effort flush before the page goes away.
  window.addEventListener('pagehide', () => flush(true));
  window.addEventListener('beforeunload', () => flush(true));
};
